import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  DotSeparatedList,
  PageHeaderWrapper,
  Text,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";

import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { readableVisitTypeMapping } from "../constants";

interface BookedVisitDetailsHeaderWrapperProps {
  isPageDataSuccess: boolean;
}

export function BookedVisitDetailsHeaderWrapper(props: BookedVisitDetailsHeaderWrapperProps) {
  const { isPageDataSuccess } = props;
  const { visit, patient } = useInHomeVisitModalsDataContext();
  const { attributes: visitAttributes } = visit ?? {};
  const { status, type } = visitAttributes ?? {};
  const { attributes: patientAttributes } = patient ?? {};
  const { externalPatientIdentifier } = patientAttributes ?? {};

  function getHeaderSubTitle() {
    if (!isPageDataSuccess || !isDefined(type)) {
      return "";
    }

    return (
      <DotSeparatedList>
        <Text inline>{readableVisitTypeMapping[type]} </Text>
        <Text inline>In Home</Text>
      </DotSeparatedList>
    );
  }

  function getHeaderTitle() {
    if (!isPageDataSuccess || !isDefined(status)) {
      return "";
    }

    return externalPatientIdentifier;
  }

  return (
    /**
     * We are setting paddingTop to 0 because we want our gradient background to go all the way to the top of the page and behind the iOS status menu.
     * We can't pass in the background to the header because it is a gradient and we need it to cover both the header part and our title part.
     * Having two gradient backgrounds on two stacked elements creates an uneven color and a line of separation.
     */
    <PageHeaderWrapper sx={{ paddingTop: 0 }}>
      <Stack
        sx={(theme) => ({
          ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(3) }),
          paddingBottom: 7,
          paddingX: 6,
          transition: theme.transitions.create(["background"]),
          background: theme.palette.homeHealth?.visit.gradient,
        })}
      >
        <Stack spacing={6}>
          <PageHeaderTopRow sx={{ px: 0 }} />
          <Stack spacing={4}>
            {isDefined(type) && (
              <Text inline semibold variant="body1">
                {getHeaderSubTitle()}
              </Text>
            )}
            <Stack direction="row" spacing={2}>
              <Title component="h3" variant="h3">
                Patient:
              </Title>
              <Title component="h3" variant="h3" sx={{ wordBreak: "break-word" }}>
                {getHeaderTitle()}
              </Title>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </PageHeaderWrapper>
  );
}
